import React, { useEffect } from 'react';
import TournamentPlayerTable from './TournamentPlayerTable';
import SelectBoxPlayer from '../../components/customLayout/selectBoxPlayer';
import TeeSelector from '../../components/customLayout/TeeSelector';
import { useTournamentStore } from '../../store/tournamentStore';
import { Player } from '../../types/types';
import { Input } from '../../components/ui/input';
import DragIcon from '../../assets/images/drag.svg';

const TournamentSetTab: React.FC = () => {
  const players = useTournamentStore((state) => state.activePlayers);

  const selectedPlayers = useTournamentStore((state) => state.selectedPlayers);
  const setSelectedPlayers = useTournamentStore(
    (state) => state.setSelectedPlayers,
  );

  const selectedErsatzspieler = useTournamentStore(
    (state) => state.selectedErsatzspieler,
  );
  const setSelectedErsatzspieler = useTournamentStore(
    (state) => state.setSelectedErsatzspieler,
  );

  const companions = useTournamentStore((state) => state.companions);
  const setCompanions = useTournamentStore((state) => state.setCompanions);

  const sortedPlayers = useTournamentStore((state) => state.sortedPlayers);
  const setSortedPlayers = useTournamentStore(
    (state) => state.setSortedPlayers,
  );

  const sortedErsatzspielers = useTournamentStore(
    (state) => state.sortedErsatzspielers,
  );
  const setSortedErsatzspielers = useTournamentStore(
    (state) => state.setSortedErsatzspielers,
  );

  const capitan = useTournamentStore((state) => state.capitan);
  const setCapitan = useTournamentStore((state) => state.setCapitan);

  const setPlayerColor = useTournamentStore((state) => state.setPlayerColor);
  const setSubstituteColor = useTournamentStore(
    (state) => state.setSubstituteColor,
  );

  const melder = useTournamentStore((state) => state.melder);
  const setMelder = useTournamentStore((state) => state.setMelder);

  const tournamentInformation = useTournamentStore(
    (state) => state.information,
  );

  const [dragState, setDragState] = React.useState<{
    index: number;
    type: 'player' | 'ersatzspieler';
    event: 'drag' | 'drop';
  } | null>({
    index: -1,
    type: 'player',
    event: 'drag',
  });

  useEffect(() => {
    if (selectedPlayers.length !== tournamentInformation.numberOfPlayers) {
      setSelectedPlayers(
        Array(tournamentInformation.numberOfPlayers).fill(null),
      );
    }

    if (
      selectedErsatzspieler.length !== tournamentInformation.numberOfSubstitute
    ) {
      setSelectedErsatzspieler(
        Array(tournamentInformation.numberOfSubstitute).fill(null),
      );
    }
  }, [
    tournamentInformation.numberOfPlayers,
    tournamentInformation.numberOfSubstitute,
  ]);

  useEffect(() => {
    const updatedSelectedPlayers = selectedPlayers.map((player) =>
      player && players.some((activePlayer) => activePlayer.id === player.id)
        ? player
        : null,
    );
    if (updatedSelectedPlayers.length) {
      setSelectedPlayers(
        updatedSelectedPlayers.map((player) =>
          player
            ? {
                ...player,
                color: player.teeColor,
                hcp: players.find((r) => r.id === player.id)?.hcp ?? 0,
              }
            : null,
        ),
      );
    }

    const updatedSelectedErsatzspieler = selectedErsatzspieler.map((player) =>
      player && players.some((activePlayer) => activePlayer.id === player.id)
        ? player
        : null,
    );
    setSelectedErsatzspieler(
      updatedSelectedErsatzspieler.map((player) =>
        player
          ? {
              ...player,
              color: player.teeColor,
              hcp: players.find((r) => r.id === player.id)?.hcp ?? 0,
            }
          : null,
      ),
    );
  }, [players]);

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
    type: 'player' | 'ersatzspieler',
  ) => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ index, type }));
    setDragState({ event: 'drag', index, type });
  };

  const handleDrop = (
    event: React.DragEvent<HTMLLIElement>,
    index: number,
    type: 'player' | 'ersatzspieler',
  ) => {
    const { index: draggedIndex, type: draggedType } = JSON.parse(
      event.dataTransfer.getData('text/plain'),
    );

    if (type !== draggedType) return;

    if (type === 'player') {
      const newItems = [...sortedPlayers];
      const [draggedItem] = newItems.splice(draggedIndex, 1);
      newItems.splice(index, 0, draggedItem);
      setSortedPlayers(newItems);
    } else {
      const newItems = [...sortedErsatzspielers];
      const [draggedItem] = newItems.splice(draggedIndex, 1);
      newItems.splice(index, 0, draggedItem);
      setSortedErsatzspielers(newItems);
    }

    setDragState({ event: 'drop', index, type });
  };

  const handleDragOver = (event: React.DragEvent<HTMLLIElement>) => {
    event.preventDefault();
  };

  const handlePlayerSelect = (index: number, player: Player) => {
    const newSelectedPlayers = [...selectedPlayers];
    if (newSelectedPlayers[index]?.id === player.id) {
      newSelectedPlayers[index] = null;
    } else {
      newSelectedPlayers[index] = player;
    }
    setSelectedPlayers(newSelectedPlayers);
  };

  const handleErsatzspielerSelect = (index: number, player: Player) => {
    const newSelectedErsatzspieler = [...selectedErsatzspieler];
    if (newSelectedErsatzspieler[index]?.id === player.id) {
      newSelectedErsatzspieler[index] = null;
    } else {
      newSelectedErsatzspieler[index] = player;
    }
    setSelectedErsatzspieler(newSelectedErsatzspieler);
  };

  return (
    <div className="grid gap-8">
      <div className="grid">
        <div className="my-2 text-lg font-medium">Spielerliste</div>
        <TournamentPlayerTable />
      </div>
      <div className="bg-white p-2 border rounded-md">
        <div className="my-2 text-lg font-medium">🏌️ Spieler</div>
        <ul>
          {sortedPlayers.map((item, index) => (
            <li
              key={item}
              onDrop={(event) => handleDrop(event, index, 'player')}
              onDragOver={handleDragOver}
              className={`flex flex-wrap items-center justify-between ${dragState?.type === 'player' && dragState.index === index && (dragState.event === 'drag' ? '' : dragState.event === 'drop' ? '' : '')}  ${index % 2 === 0 ? 'bg-gray-100' : ''}`}
            >
              <div className="p-2 flex items-center flex-1 gap-2 min-w-[120px]">
                <div
                  draggable
                  onDragStart={(event) =>
                    handleDragStart(event, index, 'player')
                  }
                  className="cursor-move text-red-500 hidden sm:block"
                >
                  <img src={DragIcon} alt="Drag" />
                </div>

                {tournamentInformation.requireTee && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 19.0001V17.0001H17V19.0001H15C14.5 19.0001 14 19.2001 13.6 19.6001C13.2 20.0001 13 20.5001 13 21.0001V22.0001H11V21.0001C11 20.5001 10.8 20.0001 10.4 19.6001C10 19.2001 9.5 19.0001 9 19.0001H7ZM11 6.0001C11 5.7001 11.1 5.5001 11.3 5.3001C11.5 5.1001 11.7 5.0001 12 5.0001C12.3 5.0001 12.5 5.1001 12.7 5.3001C12.9 5.5001 13 5.7001 13 6.0001C13 6.3001 12.9 6.5001 12.7 6.7001C12.5 6.9001 12.3 7.0001 12 7.0001C11.7 7.0001 11.5 6.9001 11.3 6.7001C11.1 6.5001 11 6.3001 11 6.0001ZM13 8.0001C13 7.7001 13.1 7.5001 13.3 7.3001C13.5 7.1001 13.7 7.0001 14 7.0001C14.3 7.0001 14.5 7.1001 14.7 7.3001C14.9 7.5001 15 7.7001 15 8.0001C15 8.3001 14.9 8.5001 14.7 8.7001C14.5 8.9001 14.3 9.0001 14 9.0001C13.7 9.0001 13.5 8.9001 13.3 8.7001C13.1 8.5001 13 8.3001 13 8.0001ZM9 8.0001C9 7.7001 9.1 7.5001 9.3 7.3001C9.5 7.1001 9.7 7.0001 10 7.0001C10.3 7.0001 10.5 7.1001 10.7 7.3001C10.9 7.5001 11 7.7001 11 8.0001C11 8.3001 10.9 8.5001 10.7 8.7001C10.5 8.9001 10.3 9.0001 10 9.0001C9.7 9.0001 9.5 8.9001 9.3 8.7001C9.1 8.5001 9 8.3001 9 8.0001ZM16.9 4.1001C15.5 2.7001 13.9 2.1001 12 2.1001C10.1 2.1001 8.4 2.8001 7.1 4.1001C5.8 5.4001 5 7.1001 5 9.0001C5 10.9001 5.7 12.6001 7 13.9001C8.3 15.2001 10 15.9001 11.9 15.9001C13.8 15.9001 15.5 15.2001 16.8 13.9001C18.1 12.6001 18.8 10.9001 18.8 9.0001C18.8 7.1001 18.3 5.4001 16.9 4.1001ZM15.5 12.5001C14.5 13.5001 13.3 14.0001 12 14.0001C10.7 14.0001 9.4 13.5001 8.5 12.5001C7.6 11.5001 7 10.4001 7 9.0001C7 7.6001 7.5 6.4001 8.5 5.5001C9.5 4.6001 10.6 4.0001 12 4.0001C13.4 4.0001 14.6 4.5001 15.5 5.5001C16.4 6.5001 17 7.6001 17 9.0001C17 10.4001 16.5 11.6001 15.5 12.5001Z"
                      fill={
                        selectedPlayers[item]?.color === 'yellow'
                          ? 'orange'
                          : selectedPlayers[item]?.color || '#cccccc'
                      }
                    />
                  </svg>
                )}

                {tournamentInformation.matchType === 'team' &&
                (index + 1) % 2 === 0
                  ? `↳ mit ${index + 1}. Spieler`
                  : `${index + 1}. Spieler`}
              </div>
              <div
                key={players
                  .filter(
                    (player) =>
                      !selectedPlayers.some((p) => p?.id === player.id) &&
                      !selectedErsatzspieler.some((p) => p?.id === player.id),
                  )
                  .map((player) => player.id)
                  .join(',')}
                className="pr-2 flex gap-2 items-center py-1 max-w-[330px]"
              >
                {tournamentInformation.requireTee && (
                  <TeeSelector
                    disabled={!selectedPlayers[item]}
                    defaultColor={
                      selectedPlayers[item]?.color as 'red' | 'green' | 'yellow'
                    }
                    onColorChange={(color) =>
                      setPlayerColor(selectedPlayers[item] as Player, color)
                    }
                  />
                )}

                <SelectBoxPlayer
                  items={players.filter(
                    (player) =>
                      (!selectedPlayers.some((p) => p?.id === player.id) &&
                        !selectedErsatzspieler.some(
                          (p) => p?.id === player.id,
                        )) ||
                      selectedPlayers[item]?.id === player.id,
                  )}
                  label="- kein Spieler -"
                  activeItems={
                    selectedPlayers[item] ? [selectedPlayers[item]] : []
                  }
                  multipleSelect={false}
                  onItemClick={(player) => handlePlayerSelect(item, player)}
                />
              </div>
            </li>
          ))}
        </ul>
        <div className="my-2 text-lg font-medium">🔄 Ersatzspieler</div>
        <ul>
          {sortedErsatzspielers.map((item, index) => (
            <li
              key={item}
              onDrop={(event) => handleDrop(event, index, 'ersatzspieler')}
              onDragOver={handleDragOver}
              className={`flex flex-wrap items-center justify-between ${dragState?.type === 'ersatzspieler' && dragState.index === index && (dragState.event === 'drag' ? '' : dragState.event === 'drop' ? '' : '')} ${index % 2 === 0 ? 'bg-gray-100' : ''}`}
            >
              <div className="p-2 flex items-center gap-4 min-w-[120px]">
                <div
                  draggable
                  onDragStart={(event) =>
                    handleDragStart(event, index, 'ersatzspieler')
                  }
                  className="cursor-move text-red-500 hidden sm:block"
                >
                  <img src={DragIcon} alt="Drag" />
                </div>
                {tournamentInformation.requireTee && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 19.0001V17.0001H17V19.0001H15C14.5 19.0001 14 19.2001 13.6 19.6001C13.2 20.0001 13 20.5001 13 21.0001V22.0001H11V21.0001C11 20.5001 10.8 20.0001 10.4 19.6001C10 19.2001 9.5 19.0001 9 19.0001H7ZM11 6.0001C11 5.7001 11.1 5.5001 11.3 5.3001C11.5 5.1001 11.7 5.0001 12 5.0001C12.3 5.0001 12.5 5.1001 12.7 5.3001C12.9 5.5001 13 5.7001 13 6.0001C13 6.3001 12.9 6.5001 12.7 6.7001C12.5 6.9001 12.3 7.0001 12 7.0001C11.7 7.0001 11.5 6.9001 11.3 6.7001C11.1 6.5001 11 6.3001 11 6.0001ZM13 8.0001C13 7.7001 13.1 7.5001 13.3 7.3001C13.5 7.1001 13.7 7.0001 14 7.0001C14.3 7.0001 14.5 7.1001 14.7 7.3001C14.9 7.5001 15 7.7001 15 8.0001C15 8.3001 14.9 8.5001 14.7 8.7001C14.5 8.9001 14.3 9.0001 14 9.0001C13.7 9.0001 13.5 8.9001 13.3 8.7001C13.1 8.5001 13 8.3001 13 8.0001ZM9 8.0001C9 7.7001 9.1 7.5001 9.3 7.3001C9.5 7.1001 9.7 7.0001 10 7.0001C10.3 7.0001 10.5 7.1001 10.7 7.3001C10.9 7.5001 11 7.7001 11 8.0001C11 8.3001 10.9 8.5001 10.7 8.7001C10.5 8.9001 10.3 9.0001 10 9.0001C9.7 9.0001 9.5 8.9001 9.3 8.7001C9.1 8.5001 9 8.3001 9 8.0001ZM16.9 4.1001C15.5 2.7001 13.9 2.1001 12 2.1001C10.1 2.1001 8.4 2.8001 7.1 4.1001C5.8 5.4001 5 7.1001 5 9.0001C5 10.9001 5.7 12.6001 7 13.9001C8.3 15.2001 10 15.9001 11.9 15.9001C13.8 15.9001 15.5 15.2001 16.8 13.9001C18.1 12.6001 18.8 10.9001 18.8 9.0001C18.8 7.1001 18.3 5.4001 16.9 4.1001ZM15.5 12.5001C14.5 13.5001 13.3 14.0001 12 14.0001C10.7 14.0001 9.4 13.5001 8.5 12.5001C7.6 11.5001 7 10.4001 7 9.0001C7 7.6001 7.5 6.4001 8.5 5.5001C9.5 4.6001 10.6 4.0001 12 4.0001C13.4 4.0001 14.6 4.5001 15.5 5.5001C16.4 6.5001 17 7.6001 17 9.0001C17 10.4001 16.5 11.6001 15.5 12.5001Z"
                      fill={
                        selectedErsatzspieler[item]?.color === 'yellow'
                          ? 'orange'
                          : selectedErsatzspieler[item]?.color || '#cccccc'
                      }
                    />
                  </svg>
                )}
                {index + 1}. Ersatzspieler
              </div>
              <div
                key={players
                  .filter(
                    (player) =>
                      !selectedPlayers.some((p) => p?.id === player.id) &&
                      !selectedErsatzspieler.some((p) => p?.id === player.id),
                  )
                  .map((player) => player.id)
                  .join(',')}
                className="pr-2 flex gap-2 items-center py-1 max-w-[330px]"
              >
                {tournamentInformation.requireTee && (
                  <TeeSelector
                    disabled={!selectedErsatzspieler[item]}
                    defaultColor={
                      selectedErsatzspieler[item]?.color as
                        | 'red'
                        | 'green'
                        | 'yellow'
                    }
                    onColorChange={(color) =>
                      setSubstituteColor(
                        selectedErsatzspieler[item] as Player,
                        color,
                      )
                    }
                  />
                )}

                <SelectBoxPlayer
                  items={players.filter(
                    (player) =>
                      (!selectedPlayers.some((p) => p?.id === player.id) &&
                        !selectedErsatzspieler.some(
                          (p) => p?.id === player.id,
                        )) ||
                      selectedErsatzspieler[item]?.id === player.id,
                  )}
                  label="- kein Ersatzspieler -"
                  activeItems={
                    selectedErsatzspieler[item]
                      ? [selectedErsatzspieler[item]]
                      : []
                  }
                  multipleSelect={false}
                  onItemClick={(player) =>
                    handleErsatzspielerSelect(item, player)
                  }
                />
              </div>
            </li>
          ))}
        </ul>

        {tournamentInformation.requireCompanion && (
          <div>
            <div className="my-2 text-lg font-medium">👥 Begleiter</div>
            {Array.from({
              length: Math.max(
                1,
                Math.ceil(
                  (selectedPlayers.filter((p) => p !== null).length +
                    selectedErsatzspieler.filter((p) => p !== null).length) /
                    3,
                ),
              ),
            }).map((_, index) => (
              <div
                className={`flex flex-wrap items-center justify-between ${index % 2 === 0 ? 'bg-gray-100' : ''}`}
                key={index}
              >
                <div className="p-2 min-w-[120px]">Begleiter {index + 1}</div>
                <div className="pr-2 py-1 max-w-[330px]">
                  <Input
                    type="string"
                    id="melder"
                    placeholder="Begleiter eingeben ..."
                    value={companions[index] || ''}
                    onChange={(e) => {
                      const newCompanions = [...companions];
                      newCompanions[index] = e.target.value;
                      setCompanions(newCompanions);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="my-2 text-lg font-medium">👨‍✈️ Kapitän</div>
        <div className="flex flex-wrap items-center justify-between bg-gray-100">
          <div className="p-2 min-w-[120px]">Kapitän</div>
          <div className="pr-2 py-1 max-w-[330px]">
            <Input
              type="string"
              id="kapitan"
              placeholder="Kapitän eingeben ..."
              value={capitan}
              onChange={(e) => setCapitan(e.target.value)}
            />
          </div>
        </div>
        <div className="my-2 text-lg font-medium">📋 Melder</div>
        <div className="flex flex-wrap items-center justify-between bg-gray-100">
          <div className="p-2 min-w-[120px]">Melder</div>
          <div className="pr-2 py-1 max-w-[330px]">
            <Input
              type="string"
              id="melder"
              placeholder="Melder eingeben ..."
              value={melder}
              onChange={(e) => setMelder(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentSetTab;
